import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiGlobe, FiRefreshCw, FiSave } from 'react-icons/fi';
import { API_URL } from '../../Api/api';
import { getAuthToken } from '../../Api/authToken';
import './AdminPages.css';
import './WebConfigEditLanguage.css';

const WebConfigEditLanguage = () => {
  const headers = () => ({ Authorization: `Bearer ${getAuthToken()}` });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modules, setModules] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [moduleId, setModuleId] = useState('');
  const [locale, setLocale] = useState('');
  const [entityId, setEntityId] = useState('');
  const [recordOptions, setRecordOptions] = useState([]);
  const [items, setItems] = useState([]);
  const [baseRecord, setBaseRecord] = useState(null);

  const selectedModule = useMemo(
    () => modules.find((m) => m.id === moduleId) || null,
    [modules, moduleId],
  );

  const loadMeta = useCallback(async () => {
    setLoading(true);
    try {
      const [modRes, langRes] = await Promise.all([
        axios.get(`${API_URL}/admin/content-translations/modules`, { headers: headers() }),
        axios.get(`${API_URL}/admin/content-translations/languages`, { headers: headers() }),
      ]);
      const mods = modRes.data?.modules || [];
      const langs = langRes.data?.languages || [];
      setModules(mods);
      setLanguages(langs);
      if (!moduleId && mods.length) setModuleId(mods[0].id);
      if (!locale && langs.length) setLocale(langs[0].code);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to load language editor');
    } finally {
      setLoading(false);
    }
  }, [moduleId, locale]);

  const loadEditor = useCallback(async (overrideEntityId) => {
    if (!moduleId || !locale) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/content-translations/editor`, {
        headers: headers(),
        params: {
          module: moduleId,
          locale,
          entity_id: overrideEntityId !== undefined ? (overrideEntityId || undefined) : (entityId || undefined),
        },
      });
      setRecordOptions(res.data?.recordOptions || []);
      setEntityId(res.data?.entity_id || '');
      setItems(res.data?.items || []);
      setBaseRecord(res.data?.baseRecord || null);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to load translation fields');
      setItems([]);
      setBaseRecord(null);
    } finally {
      setLoading(false);
    }
  }, [moduleId, locale, entityId]);

  useEffect(() => { loadMeta(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (moduleId && locale) loadEditor('');
  }, [moduleId, locale]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRecordChange = (nextId) => {
    setEntityId(nextId);
    setLoading(true);
    axios.get(`${API_URL}/admin/content-translations/editor`, {
      headers: headers(),
      params: { module: moduleId, locale, entity_id: nextId },
    }).then((res) => {
      setItems(res.data?.items || []);
      setBaseRecord(res.data?.baseRecord || null);
    }).catch((e) => {
      toast.error(e.response?.data?.error || 'Failed to load record');
    }).finally(() => setLoading(false));
  };

  const handleItemChange = (path, value) => {
    setItems((prev) => prev.map((row) => (
      row.path === path ? { ...row, translatedValue: value } : row
    )));
  };

  const saveTranslations = async () => {
    if (!moduleId || !locale || !entityId) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/content-translations/editor`, {
        module: moduleId,
        locale,
        entity_id: entityId,
        items,
      }, { headers: headers() });
      toast.success('Translations saved — changes appear on web/mobile immediately');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to save translations');
    } finally {
      setSaving(false);
    }
  };

  const groupedModules = useMemo(() => {
    const groups = {};
    modules.forEach((m) => {
      if (!groups[m.group]) groups[m.group] = [];
      groups[m.group].push(m);
    });
    return groups;
  }, [modules]);

  return (
    <div className="admin-page wcel-page">
      <div className="page-header">
        <div>
          <h1><FiGlobe style={{ marginRight: 8 }} />Web Config Edit Language</h1>
          <p>English is the base language (edit in Web Config). Add translations here for other languages.</p>
        </div>
        <button type="button" className="btn-secondary" onClick={loadEditor} disabled={loading}>
          <FiRefreshCw /> Refresh
        </button>
      </div>

      <div className="admin-card wcel-controls">
        <div className="wcel-row">
          <label>
            Module
            <select value={moduleId} onChange={(e) => setModuleId(e.target.value)}>
              {Object.entries(groupedModules).map(([group, list]) => (
                <optgroup key={group} label={group}>
                  {list.map((m) => (
                    <option key={m.id} value={m.id}>{m.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>

          <label>
            Language
            <select value={locale} onChange={(e) => setLocale(e.target.value)}>
              {languages.map((l) => (
                <option key={l.code} value={l.code}>{l.nativeName || l.name} ({l.code})</option>
              ))}
            </select>
          </label>

          {recordOptions.length > 0 && !selectedModule?.singleton && (
            <label>
              Record
              <select value={entityId} onChange={(e) => handleRecordChange(e.target.value)}>
                {recordOptions.map((r) => (
                  <option key={r.entity_id} value={r.entity_id}>{r.label}</option>
                ))}
              </select>
            </label>
          )}
        </div>

        {selectedModule && (
          <p className="wcel-hint">
            Base content (English): <strong>{selectedModule.label}</strong>
            {selectedModule.singleton ? ' — singleton' : ` — ${recordOptions.length} record(s)`}
          </p>
        )}
      </div>

      <div className="admin-card wcel-editor">
        {loading ? (
          <div className="skeleton" style={{ height: 240, borderRadius: 8 }} />
        ) : items.length === 0 ? (
          <div className="empty-state">
            <FiGlobe size={40} />
            <h3>No translatable fields</h3>
            <p>
              {recordOptions.length === 0
                ? 'Add English content in Web Config first, then return here to translate it.'
                : 'Select a record above or add English label/placeholder text in Web Config first.'}
            </p>
          </div>
        ) : (
          <>
            <div className="wcel-table-wrap">
              <table className="admin-hybrid-table wcel-table">
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>English (base)</th>
                    <th>Translation ({locale})</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.path}>
                      <td><code>{row.path}</code></td>
                      <td className="wcel-base">{row.baseValue}</td>
                      <td>
                        <textarea
                          rows={Math.min(4, Math.max(2, Math.ceil((row.baseValue || '').length / 48)))}
                          value={row.translatedValue}
                          onChange={(e) => handleItemChange(row.path, e.target.value)}
                          placeholder={`Enter ${locale} translation…`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="wcel-actions">
              <button type="button" className="btn-primary" onClick={saveTranslations} disabled={saving}>
                <FiSave /> {saving ? 'Saving…' : 'Save Translations'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default WebConfigEditLanguage;
